import { Avatar } from "@mui/material";
import { Image } from "@imagekit/react";
import { toIkSrc } from "../../helpers/imagekit";

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0]?.[0] ?? "?").toUpperCase();
}

/**
 * Avatar that shows the person's photo when available, otherwise renders
 * initials against the school's primary colour.
 *
 * Photos are served via ImageKit for automatic resizing, format conversion,
 * and face-aware cropping.
 *
 * Props:
 *   src   – photo URL (optional)
 *   name  – full name used to derive initials
 *   sx    – forwarded to MUI Avatar
 *   …rest – any other MUI Avatar props
 */
export default function UserAvatar({ src, name, sx, ...props }) {
  const initials = getInitials(name);

  if (src) {
    // Render via ImageKit for optimised delivery.
    // sx.width/height are typically set as numbers by callers (e.g. 40, 80).
    // We parse them to pass as numeric dimensions to IK for the transformation.
    const rawW = typeof sx?.width === "number" ? sx.width : 80;
    const rawH = typeof sx?.height === "number" ? sx.height : 80;
    // Use 2× for HiDPI screens; ImageKit auto-converts to WebP/AVIF.
    const size = Math.max(rawW, rawH) * 2;

    return (
      <Avatar
        sx={{ bgcolor: "grey.200", overflow: "hidden", ...sx }}
        {...props}
      >
        <Image
          src={toIkSrc(src)}
          alt={name}
          width={rawW}
          height={rawH}
          transformation={[{ width: size, height: size, cropMode: "maintain_ratio", focus: "face" }]}
          loading="lazy"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </Avatar>
    );
  }

  return (
    <Avatar
      alt={name}
      sx={{ bgcolor: "primary.main", ...sx }}
      {...props}
    >
      {initials}
    </Avatar>
  );
}

